import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subscription, BehaviorSubject, timer } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
  filter,
} from 'rxjs/operators';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';

@Component({
  selector: 'app-offline-indicator',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="offline-indicator"
      [class.visible]="showBanner$ | async"
      [class.hidden]="!(showBanner$ | async)"
      [class.fade-out]="isFadingOut$ | async"
    >
      <div class="offline-content">
        <div class="offline-icon">📡</div>
        <div class="offline-text">
          <h3>Ingen internetforbindelse</h3>
          <p>
            Du er i øjeblikket offline. Nogle funktioner er muligvis ikke
            tilgængelige.
          </p>
          <p class="retry-info">
            Appen vil automatisk prøve igen, når forbindelsen er genoprettet.
          </p>
        </div>
        <div class="offline-spinner"></div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .offline-indicator {
        position: fixed;
        top: 12px;
        left: 50%;
        transform: translate(-50%, -20px);
        z-index: 10000;
        background: #1f2937;
        color: #f9fafb;
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        padding: 10px 14px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        transition: transform 200ms ease, opacity 200ms ease;
        opacity: 0;
        pointer-events: none;
      }

      .offline-indicator.visible {
        transform: translate(-50%, 0);
        opacity: 1;
      }

      .offline-indicator.hidden {
        transform: translate(-50%, -20px);
        opacity: 0;
      }

      .offline-indicator.fade-out {
        opacity: 0;
      }

      .offline-content {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .offline-icon {
        font-size: 16px;
        line-height: 1;
        filter: grayscale(0.1);
      }

      .offline-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .offline-text h3 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.2px;
      }

      .offline-text p {
        margin: 0;
        font-size: 12px;
        opacity: 0.85;
      }

      .retry-info {
        font-size: 11px !important;
        opacity: 0.7 !important;
      }

      .offline-spinner {
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.25);
        border-top: 2px solid #f9fafb;
        border-radius: 50%;
        animation: spin 900ms linear infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @media (max-width: 768px) {
        .offline-indicator {
          top: 8px;
          border-radius: 10px;
          padding: 8px 12px;
        }
        .offline-text h3 {
          font-size: 13px;
        }
        .offline-text p {
          font-size: 11.5px;
        }
      }
    `,
  ],
})
export class OfflineIndicatorComponent implements OnInit, OnDestroy {
  private isOnline$: Observable<boolean>;
  private subscription?: Subscription;

  // Enhanced banner state management
  private showBannerSubject = new BehaviorSubject<boolean>(false);
  public showBanner$ = this.showBannerSubject.asObservable();

  private isFadingOutSubject = new BehaviorSubject<boolean>(false);
  public isFadingOut$ = this.isFadingOutSubject.asObservable();

  // Timing controls
  private readonly SHOW_DELAY = 2000; // Wait 2 seconds before showing banner
  private readonly HIDE_DELAY = 1000; // Wait 1 second before hiding banner
  private readonly FADE_DURATION = 500; // Fade out duration

  private showTimer?: any;
  private hideTimer?: any;
  private lastConnectivityState?: boolean;

  constructor(private connectivityService: ConnectivityService) {
    this.isOnline$ = this.connectivityService.getConnectivityStatus();
  }

  ngOnInit(): void {

    // Subscribe to connectivity changes with enhanced debouncing and state management
    this.subscription = this.isOnline$
      .pipe(
        distinctUntilChanged(),
        tap((isOnline) => {
        }),
        debounceTime(500) // Additional debouncing to prevent rapid state changes
      )
      .subscribe((isOnline) => {
        this.handleConnectivityChange(isOnline);
      });
  }

  private handleConnectivityChange(isOnline: boolean): void {
    // Clear any existing timers
    this.clearTimers();

    // Reset fade state
    this.isFadingOutSubject.next(false);

    if (!isOnline) {
      // Show banner after delay to avoid showing for temporary connectivity blips
      this.showTimer = setTimeout(() => {
        // Double-check connectivity before showing
        if (!this.connectivityService.isConnected()) {
          this.showBannerSubject.next(true);
        }
      }, this.SHOW_DELAY);
    } else {
      // Online - handle banner hiding with fade effect
      if (this.showBannerSubject.value) {
        this.hideBannerWithFade();
      }
    }

    this.lastConnectivityState = isOnline;
  }

  private hideBannerWithFade(): void {
    // Start fade out animation
    this.isFadingOutSubject.next(true);

    // Hide banner after fade animation completes
    this.hideTimer = setTimeout(() => {
      this.showBannerSubject.next(false);
      this.isFadingOutSubject.next(false);
    }, this.FADE_DURATION);
  }

  private clearTimers(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = undefined;
    }

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }
  }

  ngOnDestroy(): void {

    // Clean up timers
    this.clearTimers();

    // Unsubscribe from connectivity changes
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
